/// <amd-module name="WorkItemsHub/DataProviders/WorkItemsHubCommandProvider" />
import { PivotBarActionHelper } from "External/NewWorkItem/NewWorkItem";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as ColumnOptionsPanelLauncher_Async from "Utils/ColumnOptionsPanelLauncher";
import { WorkItemsHubDeleteHelper } from "Utils/WorkItemsHubDeleteHelper";
import { WorkItemsHubReportUtils } from "Utils/WorkItemsHubReportUtils";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IPivotBarAction } from "VSSUI/PivotBar";
import { VssIconType } from "VSSUI/VssIcon";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemsHubNavData, IWorkItemsHubNavDataService } from "Wit/Common/WorkItemsHubNavDataService/IWorkItemsHubNavDataService";
import { CreationMode, IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { getFieldFriendlyName } from "WorkItemsHub//Utils/WorkItemsHubDataUtils";
import { ActionsCreator } from "WorkItemsHub/Actions/ActionsCreator";
import { IWorkItemsTabContentData } from "WorkItemsHub/Components/IWorkItemsTabContentData";
import { IWorkItemsGridRow } from "WorkItemsHub/DataContracts/IWorkItemsGridData";
import { WorkItemsHubColumnOption, WorkItemsHubPermissionsData, WorkItemsHubSortOption } from "WorkItemsHub/Generated/Contracts";
import * as Resources from "WorkItemsHub/Resources";
import { IWorkItemsHubFilterDataSource } from "WorkItemsHub/Stores/IWorkItemsHubFilterDataSource";
import { WorkItemsHubStore } from "WorkItemsHub/Stores/WorkItemsHubStore";
import * as NavigationUtils from "WorkItemsHub/Utils/NavigationUtils";
import { UsageTelemetryHelper } from "WorkItemsHub/Utils/Telemetry";
import * as UrlUtils from "WorkItemsHub/Utils/UrlUtils";

export class WorkItemsHubCommandProvider {
    public static readonly columnOptionsCommandKey = "column-options";
    public static readonly recycleBinCommandKey = "recycle-bin";
    public static readonly importWorkItemsCommandKey = "import-workitems";
    public static readonly openQueryCommandKey = "open-in-queries";
    public static readonly copyToClipboardCommandKey = "copy-to-clipboard";
    public static readonly emailCommandKey = "email";
    public static readonly deleteCommandKey = "delete";

    private readonly _openFilteredInQueriesAction: IPivotBarAction = {
        key: WorkItemsHubCommandProvider.openQueryCommandKey,
        name: Resources.OpenFilteredInQueriesCommand,
        important: true,
        iconProps: { iconName: "bowtie-arrow-open", iconType: VssIconType.bowtie },
        onClick: () => {
            this._onOpenQueryClick(this._pageContext);
        }
    };

    private readonly _openInQueriesAction: IPivotBarAction = {
        key: WorkItemsHubCommandProvider.openQueryCommandKey,
        name: Resources.OpenInQueriesCommand,
        important: true,
        iconProps: { iconName: "bowtie-arrow-open", iconType: VssIconType.bowtie },
        onClick: () => {
            this._onOpenQueryClick(this._pageContext);
        }
    };

    private readonly _importWorkItemsAction: IPivotBarAction = {
        key: WorkItemsHubCommandProvider.importWorkItemsCommandKey,
        name: Resources.ImportWorkItems,
        important: true,
        iconProps: { iconName: "Upload", iconType: VssIconType.fabric },
        onClick: () => {
            this._onImportWorkItemsClick(this._pageContext);
        }
    };

    private readonly _recycleBinAction: IPivotBarAction = {
        key: WorkItemsHubCommandProvider.recycleBinCommandKey,
        name: Resources.RecycleBin,
        important: true,
        iconProps: { iconName: "bowtie-recycle-bin", iconType: VssIconType.bowtie },
        onClick: () => {
            this._onRecycleBinClick(this._pageContext);
        }
    };

    private _tabContentData: IWorkItemsTabContentData = null;
    private _deleteCommandHelper: WorkItemsHubDeleteHelper = null;
    private _addNewWorkItemPrimary: IPivotBarAction;
    private _columnOptionsPanelLauncher: ColumnOptionsPanelLauncher_Async.ColumnOptionsPanelLauncher = null;

    constructor(
        private _pageContext: IVssPageContext,
        private _store: WorkItemsHubStore,
        private _hubViewState: IVssHubViewState,
        private _actionCreator: ActionsCreator
    ) {
        this._deleteCommandHelper = new WorkItemsHubDeleteHelper(_pageContext, _actionCreator);
        this._addNewWorkItemPrimary = PivotBarActionHelper.getNewWorkItemPivotBarAction(this._pageContext, {
            addNewItem: this._onAddNewItem,
            important: true
        });
        this._columnOptionsPanelLauncher = new ColumnOptionsPanelLauncher_Async.ColumnOptionsPanelLauncher(_actionCreator, _pageContext);
    }

    /**
     * Sets the current tab data.
     * @param tabData The data containing information about tab content.
     */
    public setTabData(tabData: IWorkItemsTabContentData): void {
        this._tabContentData = tabData;
    }

    /**
     * Gets commands for work items hub.
     * @param permission permission set for work items hub
     * @param isSupportedFeature whether current page is supported or not.
     * @returns Returns commands for work items hub.
     */
    public getCommands(permission: WorkItemsHubPermissionsData, isSupportedFeature: boolean): IPivotBarAction[] {
        if (!permission) {
            return [];
        }

        const { hasFilter } = this._tabContentData;
        const pivotBarActions: IPivotBarAction[] = [];

        if (permission.newWorkItem.hasPermission) {
            pivotBarActions.push(this._addNewWorkItemPrimary);
        }

        if (permission.query.hasPermission) {
            if (isSupportedFeature) {
                const openQueryCommand: IPivotBarAction = hasFilter ? this._openFilteredInQueriesAction : this._openInQueriesAction;
                pivotBarActions.push(openQueryCommand);
                pivotBarActions.push(this._columnOptionsAction);
            }

            if (isFeatureFlagEnabled(this._pageContext, "WebAccess.WorkItemTracking.EnableCSVImport", false)) {
                pivotBarActions.push(this._importWorkItemsAction);
            }

            pivotBarActions.push(this._recycleBinAction);
        }

        return pivotBarActions;
    }

    /**
     * Gets context menu items for work items hub.
     * @param permission work items hub permissions data
     * @param targetRow the row where the context menu is targeted
     * @returns Returns context menu items if there are any, undefined if there aren't any.
     */
    public getContextMenuItems(
        pageContext: IVssPageContext,
        permission: WorkItemsHubPermissionsData,
        targetRow?: IWorkItemsGridRow
    ): IContextualMenuItem[] {
        const selectionIds = this._tabContentData.selectionIds ? [...this._tabContentData.selectionIds] : [];

        if (selectionIds.length === 0) {
            return undefined;
        }

        const items: IContextualMenuItem[] = [];
        if (permission.query.hasPermission) {
            items.push({
                name: Resources.OpenSelectionInQueriesCommand,
                key: WorkItemsHubCommandProvider.openQueryCommandKey,
                onClick: () => {
                    this._onOpenSelectedInQueryClick(pageContext);
                },
                iconProps: { className: "bowtie-icon bowtie-arrow-open" }
            });
        }

        items.push({
            name: Resources.CopyToClipboard,
            key: WorkItemsHubCommandProvider.copyToClipboardCommandKey,
            title: Resources.CommandBarTitle_CopyWorkItems,
            onClick: (e?: React.MouseEvent<HTMLElement>, menuItem?: IContextualMenuItem) => {
                this.copyWorkItemsToClipboard(pageContext, false, e, menuItem && menuItem.data);
            },
            data: targetRow,
            iconProps: { className: "bowtie-icon bowtie-edit-copy" }
        });

        if (permission.sendEmail.hasPermission) {
            items.push({
                name: Resources.EmailSelectedWorkItems,
                key: WorkItemsHubCommandProvider.emailCommandKey,
                onClick: () => {
                    this._onEmailWorkItemsClick(pageContext);
                },
                iconProps: { className: "bowtie-icon bowtie-mail-message" }
            });
        }

        const dataSource = this.getHubFilterDataSource();
        const shouldShowDeleteCommand = this.getDeleteHelper().shouldShowDeleteCommand(dataSource, selectionIds);

        if (shouldShowDeleteCommand) {
            items.push({
                name: Resources.DeleteSelectedWorkItems,
                key: WorkItemsHubCommandProvider.deleteCommandKey,
                title: Resources.CommandBarTitle_DeleteWorkItems,
                onClick: () => {
                    this.deleteWorkItems(pageContext, false);
                },
                iconProps: { className: "bowtie-icon bowtie-trash" }
            });
        }

        return items;
    }

    /**
     * Copies the selected work items to clipboard.
     * @param keyboardShortcutEvent Indicates whether it was invoked using keyboard shortcut or not, used for telemetry
     * @param e Mouse event
     * @param targetRow Target row
     */
    public copyWorkItemsToClipboard(
        pageContext: IVssPageContext,
        keyboardShortcutEvent: boolean,
        e?: React.MouseEvent<HTMLElement>,
        targetRow?: IWorkItemsGridRow
    ) {
        if (this._tabContentData.selectionIds.length === 0) {
            return;
        }

        const dataSource = this.getHubFilterDataSource();
        const filteredData = dataSource.getFilteredData();
        const fieldReferenceNames = dataSource.getDisplayedFieldReferenceNames();
        const fieldFriendlyNames = fieldReferenceNames.map(fieldReferenceName =>
            getFieldFriendlyName(fieldReferenceName, filteredData.processSettings)
        );
        const fieldValues = dataSource.getFieldValues(this._tabContentData.selectionIds, fieldReferenceNames);
        WorkItemsHubReportUtils.copyWorkItemsToClipboard(fieldReferenceNames, fieldFriendlyNames, fieldValues, pageContext, e, targetRow);
        UsageTelemetryHelper.publishCopyToClipboardTelemetry(pageContext, this._getSelectedTabId(), keyboardShortcutEvent);
    }

    /**
     * Deletes the selected work items.
     * @param keyboardShortcutEvent Indicates whether it was invoked using keyboard shortcut or not, used for telemetry
     */
    public deleteWorkItems(pageContext: IVssPageContext, keyboardShortcutEvent: boolean): void {
        const selectedWorkItemIds = this._tabContentData.selectionIds;
        const deleteHelper = this.getDeleteHelper();
        const dataSource = this.getHubFilterDataSource();
        if (deleteHelper.shouldShowDeleteCommand(dataSource, selectedWorkItemIds)) {
            const tabId = this._getSelectedTabId();
            deleteHelper.deleteWorkItems(pageContext, tabId, selectedWorkItemIds);
            UsageTelemetryHelper.publishDeleteTelemetry(pageContext, tabId, keyboardShortcutEvent);
        }
    }

    /**
     * Returns the WorkItemsHubDeleteHelper, public for unit testing.
     */
    public getDeleteHelper(): WorkItemsHubDeleteHelper {
        return this._deleteCommandHelper;
    }

    public getHubFilterDataSource(): IWorkItemsHubFilterDataSource {
        return this._store.getHubFilterDataSource(this._getSelectedTabId());
    }

    private _getSelectedTabId(): string {
        return this._hubViewState.selectedPivot.value.toLowerCase();
    }

    private _onColumnOptionsClick = () => {
        UsageTelemetryHelper.publishColumnOptionsCommand(this._pageContext, this._getSelectedTabId());
        const dataSource = this.getHubFilterDataSource();
        const tabId = this._getSelectedTabId();
        const currentColumns: WorkItemsHubColumnOption[] = dataSource.getCurrentColumnOptions();
        const sortOptions: WorkItemsHubSortOption[] = dataSource.getSortOptions();
        const version: number = dataSource.getCurrentColumnSettingsVersion();

        this._columnOptionsPanelLauncher.openColumnOptionPanel(tabId, currentColumns, sortOptions, version);
    };

    private _onImportWorkItemsClick = (pageContext: IVssPageContext) => {
        UsageTelemetryHelper.publishImportWorkItemsCommand(pageContext, this._getSelectedTabId());
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectName: string = tfsPageData.project.name;
        window.open(UrlUtils.getImportWorkItemsUrl(pageContext, projectName));
    };

    private _onRecycleBinClick = (pageContext: IVssPageContext) => {
        UsageTelemetryHelper.publishRecycleBinCommand(pageContext, this._getSelectedTabId());
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectName: string = tfsPageData.project.name;
        window.open(UrlUtils.getRecycleBinUrl(pageContext, projectName));
    };

    private _onOpenQueryClick = (pageContext: IVssPageContext) => {
        const dataSource = this.getHubFilterDataSource();
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectName: string = tfsPageData.project.name;
        NavigationUtils.openInQueries(pageContext, projectName, this._getSelectedTabId(), dataSource);
    };

    private _onOpenSelectedInQueryClick = (pageContext: IVssPageContext) => {
        const dataSource = this.getHubFilterDataSource();
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectName: string = tfsPageData.project.name;
        NavigationUtils.openInQueries(pageContext, projectName, this._getSelectedTabId(), dataSource, this._tabContentData.selectionIds);
    };

    private _onAddNewItem = (
        pageContext: IVssPageContext,
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        item: IPivotBarAction,
        workItemTypeName: string
    ) => {
        const service = pageContext.getService<IWorkItemService>("work-item-service");
        const tabId = this._getSelectedTabId();
        const navigationDataService = pageContext.getService<IWorkItemsHubNavDataService>("IWorkItemsHubNavDataService");
        navigationDataService.setNavigationData({ tabId } as IWorkItemsHubNavData);

        service.createWorkItem(item.key, CreationMode.FullPagePreferred);

        e.preventDefault();
        e.stopPropagation();
    };

    private _onEmailWorkItemsClick = (pageContext: IVssPageContext): void => {
        const selectedIds = this._tabContentData.selectionIds;
        if (selectedIds.length === 0) {
            return;
        }

        const dataSource = this.getHubFilterDataSource();
        const fieldReferenceNames = dataSource.getDisplayedFieldReferenceNames();
        let subject: string;

        if (selectedIds.length === 1) {
            // Construct a subject, otherwise just use the default
            const id = selectedIds[0];
            subject = `${selectedIds[0]} - ${dataSource.getValue(id, CoreFieldRefNames.Title)}`;
        }

        WorkItemsHubReportUtils.openSendEmailDialog(pageContext, fieldReferenceNames, selectedIds, subject);
        UsageTelemetryHelper.publishEmailTelemetry(pageContext, this._getSelectedTabId());
    };

    private readonly _columnOptionsAction: IPivotBarAction = {
        key: WorkItemsHubCommandProvider.columnOptionsCommandKey,
        name: Resources.ColumnOptions,
        important: true,
        iconProps: { iconName: "Repair" },
        onClick: this._onColumnOptionsClick
    };
}
